import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Plug } from "lucide-react";

import { GuideView } from "@/components/admin/integration/GuideView";
import { LoadApiReferenceView } from "@/components/admin/integration/LoadApiReferenceView";
import { LoadDictionaryView } from "@/components/admin/integration/LoadDictionaryView";
import { ManifestView } from "@/components/admin/integration/ManifestView";
import {
  ReferenceDataTabs,
  ReferenceDataTabsContent,
  ReferenceDataTabsList,
  ReferenceDataTabsTrigger,
} from "@/components/admin/reference-data/ReferenceDataTabs";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem } from "@/components/brand/Hero";
import {
  useLoadApiReference,
  useLoadDictionary,
  useManifestDictionary,
} from "@/hooks/useIntegrationDocs";

const TABS = ["guide", "manifest", "dictionary", "api"] as const;
type IntegrationTab = (typeof TABS)[number];

/**
 * Documentação de integração com o myGROW (#1782): o que quem integra uma origem precisa para
 * mandar os dados dela. O guia vem com o frontend; o dicionário e a referência saem do código pela
 * api, então batem com o que ela aceita.
 */
export default function AdminIntegration() {
  const { t } = useTranslation();
  // A aba vive na URL, como no Reference Data: o link de uma aba abre direto nela.
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab: IntegrationTab = TABS.find((name) => name === requested) ?? "guide";
  const setTab = (next: string) =>
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        if (next === "guide") merged.delete("tab");
        else merged.set("tab", next);
        merged.delete("chapter");
        return merged;
      },
      { replace: true },
    );

  const dictionary = useLoadDictionary();
  const reference = useLoadApiReference();
  const manifest = useManifestDictionary();

  const tables = dictionary.data?.domains.flatMap((d) => d.tables) ?? [];
  const fieldCount = tables.reduce((acc, table) => acc + table.fields.length, 0);
  const routeCount = Object.values(reference.data?.paths ?? {}).reduce(
    (acc, operations) => acc + Object.keys(operations).length,
    0,
  );

  const kpis = [
    { label: t("Domains"), value: (dictionary.data?.domains.length ?? 0).toLocaleString() },
    { label: t("Tables"), value: tables.length.toLocaleString(), gold: true },
    { label: t("Fields"), value: fieldCount.toLocaleString() },
    { label: t("Routes"), value: routeCount.toLocaleString() },
  ];

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Plug />}
        overline={t("ADMIN · INTEGRATION")}
        title={t("Integration")}
        meta={<HeroMetaItem k={t("Load API")} v={dictionary.data?.api_version ?? "v1"} />}
        kpis={kpis}
      />

      <div className="p-8">
        <p className="text-[13px] text-[#596b85] max-w-3xl mb-6">
          {t(
            "Everything an integrator needs to send a source system's data to myGROW: the guide, the manifest, the load dictionary, with every table and field, and the routes of the load API.",
          )}
        </p>
        <ReferenceDataTabs value={tab} onValueChange={setTab}>
          <ReferenceDataTabsList>
            <ReferenceDataTabsTrigger value="guide">{t("Guide")}</ReferenceDataTabsTrigger>
            <ReferenceDataTabsTrigger value="manifest">{t("Manifest")}</ReferenceDataTabsTrigger>
            <ReferenceDataTabsTrigger value="dictionary">{t("Load dictionary")}</ReferenceDataTabsTrigger>
            <ReferenceDataTabsTrigger value="api">{t("API reference")}</ReferenceDataTabsTrigger>
          </ReferenceDataTabsList>
          <ReferenceDataTabsContent value="guide" className="mt-5">
            <GuideView />
          </ReferenceDataTabsContent>
          <ReferenceDataTabsContent value="manifest" className="mt-5">
            {manifest.data ? (
              <ManifestView manifest={manifest.data} />
            ) : (
              <LoadState isError={manifest.isError} />
            )}
          </ReferenceDataTabsContent>
          <ReferenceDataTabsContent value="dictionary" className="mt-5">
            {dictionary.data ? (
              <LoadDictionaryView dictionary={dictionary.data} />
            ) : (
              <LoadState isError={dictionary.isError} />
            )}
          </ReferenceDataTabsContent>
          <ReferenceDataTabsContent value="api" className="mt-5">
            {reference.data ? (
              <LoadApiReferenceView reference={reference.data} />
            ) : (
              <LoadState isError={reference.isError} />
            )}
          </ReferenceDataTabsContent>
        </ReferenceDataTabs>
      </div>
    </div>
  );
}

function LoadState({ isError }: { isError: boolean }) {
  const { t } = useTranslation();
  return (
    <p className="text-center py-12 text-[#7b8799] text-sm">
      {isError ? t("Could not load the integration documentation.") : t("Loading…")}
    </p>
  );
}
